import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';

export type FileType = 'pdf' | 'image' | 'unknown';

export interface UseFileViewerReturn {
  fileUrl: string | null;
  isLoadingFile: boolean;
  fileError: string | null;
  fileType: FileType;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for handling file viewing functionality
 * Manages file URL fetching, loading states, and error handling
 */
export const useFileViewer = (invoiceId?: string): UseFileViewerReturn => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(true);
  const [fileError, setFileError] = useState<string | null>(null);

  /**
   * Determines file type based on MIME type and filename
   */
  const getFileType = (mimeType?: string, filename?: string): FileType => {
    if (mimeType) {
      if (mimeType === 'application/pdf') {
        return 'pdf';
      }
      if (mimeType.startsWith('image/')) {
        return 'image';
      }
    }

    const filenameLower = filename?.toLowerCase() || '';
    if (filenameLower.endsWith('.pdf')) {
      return 'pdf';
    }
    if (filenameLower.match(/\.(png|jpe?g|tiff?)$/)) {
      return 'image';
    }

    return 'unknown';
  };

  /**
   * Fetches the file URL from the API
   */
  const fetchFileUrl = useCallback(async (signal?: AbortSignal) => {
    if (!invoiceId) {
      setIsLoadingFile(false);
      return;
    }

    try {
      setIsLoadingFile(true);
      setFileError(null);

      const data = await apiClient.get<{ download_url?: string }>(
        `/invoices/${invoiceId}/download`,
        signal ? { signal } : undefined
      );

      if (signal?.aborted) {
        return;
      }

      if (!data?.download_url) {
        throw new Error('Invoice download URL not available');
      }

      setFileUrl(data.download_url);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching file URL:', error);
      setFileError(error instanceof Error ? error.message : 'Failed to load file');
      setFileUrl(null);
    } finally {
      if (signal?.aborted) {
        return;
      }
      setIsLoadingFile(false);
    }
  }, [invoiceId]);

  // Fetch file URL when invoiceId changes
  useEffect(() => {
    if (!invoiceId) {
      setFileUrl(null);
      setIsLoadingFile(false);
      setFileError(null);
      return undefined;
    }

    const controller = new AbortController();
    void fetchFileUrl(controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchFileUrl, invoiceId]);

  const fileType = getFileType(undefined, ''); // This would need the actual file data to work properly

  return {
    fileUrl,
    isLoadingFile,
    fileError,
    fileType,
    refetch: () => fetchFileUrl()
  };
};
